import { useState, useMemo } from "react";
import { ChevronRight } from "lucide-react";

const ROLE_EMOJI = {
  juriste: "⚖️",
  parlementaire: "🏛",
  gouvernement: "🏢",
  ong: "🤝",
  chercheur: "🎓",
  citoyen: "👤",
};

/**
 * Flatten the tree into an ordered list of rows.
 * Each row = { node, depth, parentPath, hasChildren }.
 * parentPath is an array of ancestor ids so we can skip rows whose any
 * ancestor is collapsed. This avoids a self-recursive React component
 * (which crashes the Babel visual-edits plugin).
 */
function flattenTree(root) {
  const rows = [];
  const stack = [{ node: root, depth: 0, parentPath: [] }];
  while (stack.length) {
    const { node, depth, parentPath } = stack.shift();
    const children = node.children || [];
    const id = node?.user?.id || `anon-${depth}-${rows.length}`;
    rows.push({
      id,
      node,
      depth,
      parentPath,
      hasChildren: children.length > 0,
    });
    // DFS via unshift so children appear right after parent
    const childPath = [...parentPath, id];
    const mapped = children.map((c) => ({
      node: c,
      depth: depth + 1,
      parentPath: childPath,
    }));
    stack.splice(0, 0, ...mapped);
  }
  return rows;
}

export default function NetworkTreeNode({ node, depth = 0, isRoot = false }) {
  // `depth` is kept in the signature for back-compat but ignored — we flatten from the passed node
  const rows = useMemo(() => flattenTree(node), [node]);

  // Default: expand only first 2 levels
  const defaultCollapsed = useMemo(() => {
    const s = new Set();
    for (const r of rows) {
      if (r.depth >= 2 && r.hasChildren) s.add(r.id);
    }
    return s;
  }, [rows]);

  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const toggle = (id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isRowHidden = (row) =>
    row.parentPath.some((pid) => collapsed.has(pid));

  return (
    <div data-testid="network-tree-root">
      {rows.map((row) => {
        if (isRowHidden(row)) return null;
        const { id, node: n, depth: d, hasChildren } = row;
        const isExpanded = !collapsed.has(id);
        const root = d === 0 && isRoot;
        const u = n.user || {};
        const badge = n.badge || {};
        return (
          <div
            key={id}
            className="relative"
            style={{ marginLeft: d * 20 }}
          >
            <div className="flex items-center gap-2 py-2">
              {hasChildren ? (
                <button
                  onClick={() => toggle(id)}
                  className="opacity-60 hover:opacity-100"
                  data-testid={`net-toggle-${u.id}`}
                >
                  <ChevronRight
                    size={14}
                    style={{
                      transform: isExpanded ? "rotate(90deg)" : "none",
                      transition: "transform 0.2s",
                    }}
                  />
                </button>
              ) : (
                <div className="w-4" />
              )}

              <div
                className="flex-1 flex items-center gap-3 p-2 rounded-sm border"
                style={{
                  borderColor: root ? "#D4A017" : "hsl(var(--border))",
                  background: root ? "rgba(212,160,23,0.05)" : "transparent",
                }}
                data-testid={`net-node-${u.id}`}
              >
                <span className="text-lg">{ROLE_EMOJI[u.role] || "👤"}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">
                    {root && (
                      <span className="text-[10px] uppercase mr-2 opacity-60">
                        Vous
                      </span>
                    )}
                    {u.name || u.email}
                  </div>
                  <div className="text-[10px] opacity-60 font-mono">
                    {u.role} · {n.stats?.count_projects || 0} projet(s) ·{" "}
                    {n.stats?.count_accepted || 0} invitation(s) acceptée(s)
                  </div>
                </div>
                <span
                  className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-sm"
                  style={{
                    background: `${badge.color}15`,
                    color: badge.color,
                  }}
                >
                  {badge.label} · {n.stats?.score_mobilisateur || 0}pts
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
